import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, translations } from "./translations";

const STORAGE_KEY = "alinen.locale";

const LanguageContext = createContext(null);

function readStoredLocale() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw && SUPPORTED_LOCALES.includes(raw)) {
      return raw;
    }
  } catch {
    // ignore
  }
  return DEFAULT_LOCALE;
}

export function LanguageProvider({ children }) {
  const [locale, setLocaleState] = useState(() => readStoredLocale());

  const setLocale = useCallback((next) => {
    if (!SUPPORTED_LOCALES.includes(next)) {
      return;
    }
    setLocaleState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore
    }
  }, []);

  const t = translations[locale] || translations[DEFAULT_LOCALE];

  useEffect(() => {
    document.documentElement.lang = locale === "fi" ? "fi" : "en";
    if (t.meta?.documentTitle) {
      document.title = t.meta.documentTitle;
    }
  }, [locale, t]);

  const value = useMemo(
    () => ({
      locale,
      setLocale,
      t,
    }),
    [locale, setLocale, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return ctx;
}
